/* eslint-disable import/prefer-default-export */
import React, { createContext, Dispatch, PropsWithChildren, SetStateAction, useMemo, useState } from 'react'

export type InitializableProviderProps<T> = PropsWithChildren<{
    initialValues: Partial<T>
}>

export function createCtx<T>(defaultValue: T) {
    type SetStateType = Dispatch<SetStateAction<typeof defaultValue>>
    type UpdateType = (newState: Partial<T>) => void

    const defaultSetState: SetStateType = () => defaultValue
    const defaultUpdate: UpdateType = (x: Partial<T>) => x

    const ctx = createContext({
        state: defaultValue,
        setState: defaultSetState,
        update: defaultUpdate,
    })

    function Provider({ children, initialValues }: InitializableProviderProps<T>) {
        const [state, setState] = useState({ ...defaultValue, ...initialValues })

        /**
         * @description upates state if state AND newState are object type. If not, will do nothing.
         * @param newState partial properties of state
         */
        const update = useMemo(
            () => (newState: Partial<T>) => {
                setState((prevState) => ({
                    ...prevState,
                    ...newState,
                }))
            },
            []
        )

        const value = useMemo(() => ({ state, setState, update }), [state, update])

        return <ctx.Provider value={value}>{children}</ctx.Provider>
    }
    return [ctx, Provider] as const // alternatively, [typeof ctx, typeof Provider]
}
